import { zCustom } from '@nucleus/lib-shape';
import { z } from 'zod';
import { blendModeSchema, fadeSchema, filterSchema, textureSchema } from '../media/blending';
import { imageScaleSchema, imageSchema, imageTileSchema, imageWebSchema } from '../media/image';
import { ColorConfigSchema, ThemeColorKey } from './colors';

// NOTE: Leave this private! Uploaded Video is coming.
const VideoSchema = z.object({
  src: z.string().optional(),
});

const ThemeColorKeySchema = z.enum(Object.values(ThemeColorKey));

const MediaImageSchema = z.object({
  scale: imageScaleSchema.optional(),
  tile: imageTileSchema.optional(),
});

const BlendingOptionsSchema = z.object({
  opacity: zCustom.range(0, 1, { name: 'Opacity' }).optional(),
  blendMode: blendModeSchema.optional(),
  fade: fadeSchema.optional(),
  fadeIntensity: zCustom.range(0, 1, { name: 'Fade Intensity' }).optional(),
  filter: filterSchema.optional(),
  texture: textureSchema.optional(),
});

export const PresetMediaKeySchema = z.enum([
  'blockBackground',
  'cardBackground',
  'cardMediaFull',
  'cardMediaInline',
  'leaderCardMediaFull',
  'leaderCardMediaInline',
  'listMedia',
  'sectionBackground',
  'sectionMedia',
]);

export type PresetMediaKey = z.infer<typeof PresetMediaKeySchema>;

export const PresetMediaOrderSchema = z.enum(['first', 'last']);

export type PresetMediaOrder = z.infer<typeof PresetMediaOrderSchema>;

export const MediaSchema = z.object({
  useColor: z.boolean().optional(),
  useImage: z.boolean().optional(),
  useVideo: z.boolean().optional(),
  color: ColorConfigSchema.pick({ hex: true }).optional(),
  image: MediaImageSchema.and(imageSchema.partial()).optional(),
  video: z
    .object({
      useImage: z.boolean().optional(),
      image: MediaImageSchema.and(imageSchema.partial()).optional(),
    })
    .and(VideoSchema)
    .optional(),
  blending: BlendingOptionsSchema.optional(),
  presetMediaKey: PresetMediaKeySchema.optional(),
  presetMediaOrder: PresetMediaOrderSchema.optional(),
  themeColorKey: ThemeColorKeySchema.optional(),
});

export type Media = z.infer<typeof MediaSchema>;

export const MediaWebSchema = z.object({
  color: ColorConfigSchema.pick({ hex: true }).optional(),
  image: MediaImageSchema.and(imageWebSchema).optional(),
  video: VideoSchema.optional(),
  blending: BlendingOptionsSchema.optional(),
  themeColorKey: ThemeColorKeySchema.optional(),
});

export type MediaWeb = z.infer<typeof MediaWebSchema>;

export const MediaOptionsSchema = z.object({
  useColor: z.boolean().optional(),
  useImage: z.boolean().optional(),
  useVideo: z.boolean().optional(),
  color: ColorConfigSchema.pick({ hex: true }).optional(),
  image: MediaImageSchema.optional(),
  video: z
    .object({
      useImage: z.boolean().optional(),
      image: MediaImageSchema.optional(),
    })
    .optional(),
  blending: BlendingOptionsSchema.optional(),
  presetMediaKey: PresetMediaKeySchema.optional(),
});

export type MediaOptions = z.infer<typeof MediaOptionsSchema>;

export const PresetMediaSchema = z.object({
  mediaOptions: MediaOptionsSchema,
  updatedAt: z.string().optional(), // ISO8601
});

export type PresetMedia = z.infer<typeof PresetMediaSchema>;

/** @deprecated */
export const PresetMediaKeys: Array<PresetMediaKey> = [
  'cardBackground',
  'cardMediaFull',
  'cardMediaInline',
  'leaderCardMediaFull',
  'leaderCardMediaInline',
  'listMedia',
  'sectionBackground',
  'sectionMedia',
] as const;
